<script setup lang="ts">
import { computed, ref, watch } from 'vue';

import { eqsApi } from '@/api/eqs';
import ExportDataButton from '@/components/ExportDataButton.vue';
import type { ModuleRecord } from '@/types/domain';
import { buildExportFilename } from '@/utils/csv-export';
import { errorMessage } from '@/utils/format';

/**
 * 控制计划正文。
 *
 * 之前这一页只有一张「控制计划 cp-47」的卡片和一个「编辑」按钮——
 * 39 条控制行明明在库里（特殊特性核对面板就是读它们算出来的），
 * 界面上却一行都看不到，等于「有控制计划但打不开」（试用反馈 8/12）。
 *
 * 这里按 AIAG 控制计划的标准表头铺开：工序 → 特性 → 公差 →
 * 评价技术 → 样本 → 控制方法 → 反应计划。同一道工序的多行合并显示工序号，
 * 和大家手上的 Excel 版一个读法。
 */
const props = defineProps<{
  controlPlan: ModuleRecord;
}>();

interface PlanRow {
  id: number;
  stepNumber: string;
  stepDescription: string;
  machine: string;
  productCharacteristic: string;
  processCharacteristic: string;
  special: string;
  specification: string;
  evaluation: string;
  sampleSize: string;
  sampleFrequency: string;
  controlMethod: string;
  reactionPlan: string;
  errorProofing: string;
  sortOrder: number;
}

const rows = ref<PlanRow[]>([]);
const loading = ref(false);
const error = ref('');

function text(source: Record<string, unknown>, key: string): string {
  const value = source[key];
  return value === null || value === undefined ? '' : String(value).trim();
}

async function load(): Promise<void> {
  loading.value = true;
  error.value = '';
  try {
    const payload = await eqsApi.childRecords('pfmea-cp', 'control-plan-rows', props.controlPlan.id);
    rows.value = (payload.items ?? [])
      .map((item) => {
        const n = (item.normalized ?? {}) as Record<string, unknown>;
        return {
          id: Number(item.id),
          stepNumber: text(n, 'step_number'),
          stepDescription: text(n, 'process_step_description') || String(item.title ?? ''),
          machine: text(n, 'machine_tooling'),
          productCharacteristic: text(n, 'product_characteristic'),
          processCharacteristic: text(n, 'process_characteristic'),
          special: text(n, 'is_special'),
          specification: text(n, 'specification_tolerance'),
          evaluation: text(n, 'evaluation_technique'),
          sampleSize: text(n, 'sample_size'),
          sampleFrequency: text(n, 'sample_frequency'),
          controlMethod: text(n, 'control_method'),
          reactionPlan: text(n, 'reaction_plan'),
          errorProofing: text(n, 'error_proofing'),
          sortOrder: Number(n.sort_order ?? 0),
        };
      })
      .sort((a, b) => a.sortOrder - b.sortOrder || a.id - b.id);
  } catch (caught) {
    rows.value = [];
    error.value = errorMessage(caught);
  } finally {
    loading.value = false;
  }
}

watch(() => props.controlPlan.id, load, { immediate: true });

/** 同一道工序只在第一行显示工序号，和纸质控制计划的读法一致 */
function showsStep(index: number): boolean {
  if (index === 0) return true;
  return rows.value[index]!.stepNumber !== rows.value[index - 1]!.stepNumber;
}

const specialCount = computed(() => rows.value.filter((row) => row.special).length);

const exportColumns = [
  { key: 'stepNumber', label: '工序号' },
  { key: 'stepDescription', label: '工序名称 / 操作描述' },
  { key: 'machine', label: '设备 / 工装' },
  { key: 'productCharacteristic', label: '产品特性' },
  { key: 'processCharacteristic', label: '过程特性' },
  { key: 'special', label: '特殊特性' },
  { key: 'specification', label: '规范 / 公差' },
  { key: 'evaluation', label: '评价 / 测量技术' },
  { key: 'sampleSize', label: '样本容量' },
  { key: 'sampleFrequency', label: '样本频率' },
  { key: 'controlMethod', label: '控制方法' },
  { key: 'reactionPlan', label: '反应计划' },
  { key: 'errorProofing', label: '防错' },
];

const exportRows = computed(() => rows.value.map((row) => ({ ...row })));
const exportFilename = computed(() => buildExportFilename([
  String(props.controlPlan.code ?? '控制计划'),
  String(props.controlPlan.title ?? ''),
]));
</script>

<template>
  <section class="cp-table">
    <header>
      <div>
        <span>CONTROL PLAN</span>
        <h3>{{ controlPlan.title }}</h3>
        <p>
          {{ controlPlan.code }} · 版本 {{ controlPlan.revision || '—' }} ·
          共 {{ rows.length }} 条控制行<template v-if="specialCount">，其中 {{ specialCount }} 条标了特殊特性</template>
        </p>
      </div>
      <ExportDataButton
        v-if="rows.length"
        label="导出这份控制计划"
        :columns="exportColumns"
        :rows="exportRows"
        :filename="exportFilename"
      />
    </header>

    <p v-if="loading" class="cp-hint">正在读取控制行…</p>
    <p v-else-if="error" class="cp-hint cp-error">控制行读取失败：{{ error }}</p>
    <p v-else-if="!rows.length" class="cp-hint">
      这份控制计划还没有控制行。可以用右上角「批量导入 / 更新」按 AIAG 模板导入，或从 PFMEA 生成。
    </p>

    <div v-else class="data-table-wrap">
      <table class="data-table">
        <thead>
          <tr>
            <th>工序</th>
            <th>设备 / 工装</th>
            <th>产品特性</th>
            <th>过程特性</th>
            <th title="CC=关键特性，SC=重要特性">特殊</th>
            <th>规范 / 公差</th>
            <th>评价 / 测量技术</th>
            <th>样本</th>
            <th>控制方法</th>
            <th>反应计划</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, index) in rows" :key="row.id" :class="{ 'cp-step-start': showsStep(index) }">
            <td class="cp-step">
              <template v-if="showsStep(index)">
                <b>{{ row.stepNumber || '—' }}</b>
                <small>{{ row.stepDescription }}</small>
              </template>
              <span v-else class="cp-same">同上</span>
            </td>
            <td>{{ row.machine || '—' }}</td>
            <td>{{ row.productCharacteristic || '—' }}</td>
            <td>{{ row.processCharacteristic || '—' }}</td>
            <td>
              <b v-if="row.special" class="cp-special">{{ row.special }}</b>
              <span v-else>—</span>
            </td>
            <td>{{ row.specification || '—' }}</td>
            <td>{{ row.evaluation || '—' }}</td>
            <td class="cp-sample">
              <span v-if="row.sampleSize || row.sampleFrequency">
                {{ row.sampleSize || '—' }} / {{ row.sampleFrequency || '—' }}
              </span>
              <span v-else>—</span>
            </td>
            <td>{{ row.controlMethod || '—' }}</td>
            <td>
              {{ row.reactionPlan || '—' }}
              <small v-if="row.errorProofing && row.errorProofing !== '无'">防错：{{ row.errorProofing }}</small>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </section>
</template>

<style scoped>
.cp-table { margin-bottom: 18px; border: 1px solid var(--line); background: var(--white); }
.cp-table > header {
  display: flex; align-items: flex-start; justify-content: space-between; gap: 14px;
  padding: 14px 16px; border-bottom: 1px solid var(--line);
}
.cp-table > header span { color: var(--blue); font: 800 10px var(--display); letter-spacing: .12em; }
.cp-table > header h3 { margin: 3px 0 4px; font-size: 16px; }
.cp-table > header p { margin: 0; color: var(--muted); font-size: 12px; }
.cp-hint { margin: 0; padding: 16px; color: var(--muted); font-size: 13px; line-height: 1.7; }
.cp-error { color: var(--risk); }
.cp-table .data-table-wrap { max-height: 520px; overflow: auto; }
.cp-table td { vertical-align: top; font-size: 12px; }
/* 工序换行处加一道分隔，一眼看出这是新的一道工序 */
.cp-step-start td { border-top: 1px solid var(--line); }
.cp-step { min-width: 150px; }
.cp-step b { display: block; color: var(--ink); font-size: 12px; }
.cp-step small { color: var(--muted); font-size: 12px; }
.cp-same { color: var(--line-strong); font-size: 12px; }
.cp-special { color: var(--risk); font-weight: 800; }
.cp-sample { white-space: nowrap; }
.cp-table td small { display: block; margin-top: 3px; color: var(--muted); }
</style>
